import { useState, useEffect, useMemo, useRef } from "preact/hooks";
import { useNavigate } from "../contexts/AppContext";

interface Template {
  name: string;
  description: string;
}

const PICK_SIZE = 6;

function pickRandom<T>(arr: T[], n: number): T[] {
  if (arr.length <= n) return arr.slice();
  const copy = arr.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

export function PortalForm() {
  const navigate = useNavigate();
  const titleRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [allTemplates, setAllTemplates] = useState<Template[]>([]);
  const [shown, setShown] = useState<Template[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState<string>("");

  useEffect(() => {
    fetch("/api/templates")
      .then((r) => r.json())
      .then((data: Template[]) => {
        const list = Array.isArray(data) ? data : [];
        setAllTemplates(list);
        setShown(pickRandom(list, PICK_SIZE));
      })
      .catch(() => setLoadErr("Could not load templates"));
  }, []);

  const shuffle = () => {
    if (allTemplates.length === 0) return;
    setShown(pickRandom(allTemplates, PICK_SIZE));
  };

  const canSubmit = useMemo(
    () => title.trim().length > 0 && !saving,
    [title, saving],
  );

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await fetch("/api/portals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          template: selected || undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        navigate(`/edit/${data.id}`, { replace: true });
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || "Save failed");
      }
    } catch {
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="container py-5">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <h1 class="display-4">Create Portal</h1>
          <form class="vstack gap-4" onSubmit={handleSubmit}>
            <div>
              <label class="form-label">Title</label>
              <input
                ref={titleRef}
                type="text"
                class="form-control form-control-lg"
                placeholder="Your name or site title"
                value={title}
                onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
                autoFocus
                required
              />
            </div>

            <div>
              <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label mb-0">Pick a starting template</label>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  onClick={shuffle}
                  disabled={allTemplates.length === 0}
                >
                  <i class="bi bi-shuffle"></i> Shuffle
                </button>
              </div>
              {loadErr ? (
                <div class="text-danger small">{loadErr}</div>
              ) : (
                <div class="row g-3">
                  {shown.map((tpl) => (
                    <div key={tpl.name} class="col-12 col-sm-6 col-lg-4">
                      <TemplateTile
                        tpl={tpl}
                        selected={selected === tpl.name}
                        onClick={() => {
                          setSelected(selected === tpl.name ? "" : tpl.name);
                          titleRef.current?.focus();
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div class="hstack justify-content-center">
              <button
                type="submit"
                class="btn btn-primary btn-lg"
                disabled={!canSubmit}
              >
                <i class="bi bi-check-lg"></i>{" "}
                {saving ? "Creating..." : "Create Portal"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function TemplateTile({
  tpl,
  selected,
  onClick,
}: {
  tpl: Template;
  selected: boolean;
  onClick: () => void;
}) {
  const previewRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Measure the preview container and scale the 600px-wide iframe to fit.
  // A pure-CSS scale(calc(100cqw / 600)) is invalid — scale() needs a
  // unitless <number> and cqw math yields a <length> — so we do it in JS.
  useEffect(() => {
    const el = previewRef.current;
    const ifr = iframeRef.current;
    if (!el || !ifr) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        if (w > 0) {
          const scale = w / 600;
          ifr.style.transform = `scale(${scale})`;
        }
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <button
      type="button"
      class={`template-tile w-100 text-start p-0 ${selected ? "selected" : ""}`}
      onClick={onClick}
      title={tpl.description}
    >
      <div ref={previewRef} class="template-tile-preview">
        <iframe
          ref={iframeRef}
          src={`/api/templates/${encodeURIComponent(tpl.name)}/preview`}
          title={tpl.name}
          loading="lazy"
          scrolling="no"
        />
      </div>
      <div class="template-tile-meta">
        <div class="fw-bold text-truncate">{tpl.name}</div>
        <div class="small text-muted text-truncate">{tpl.description}</div>
      </div>
    </button>
  );
}
